import * as React from 'react'

import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import useStore from '@/stores/useStore'

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

function formatNumberValue(value) {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

function NumberInput(props) {
  const {
    value,
    onValueChange,
    min,
    max,
    integer = false,
    emptyValue,
    emptyMessage = 'Enter a number.',
    invalidMessage = 'Enter a valid number.',
    minMessage,
    maxMessage,
    className,
    containerClassName,
    errorClassName,
    showError = true,
    onBlur,
    inputMode,
    validationId,
    validationLabel,
    validationTarget,
    validationActive = true,
    ...inputProps
  } = props
  const emptyIsValid = hasOwn(props, 'emptyValue')
  const generatedId = React.useId()
  const inputErrorId = validationId || inputProps.id || inputProps['aria-label'] || generatedId
  const inputErrorLabel = validationLabel || inputProps['aria-label'] || inputProps.id || 'Number input'
  const setInputError = useStore((state) => state.setInputError)
  const storedInputError = useStore((state) => state.inputErrors[inputErrorId])
  const inputValidationResetKey = useStore((state) => state.inputValidationResetKey)
  const lastValidationResetKey = React.useRef(inputValidationResetKey)
  const previousStoredMessageRef = React.useRef('')
  const skipNextErrorPublishRef = React.useRef(false)
  const skipNextBlurCommitRef = React.useRef(false)
  const [draft, setDraft] = React.useState(formatNumberValue(value))
  const [editing, setEditing] = React.useState(false)
  const [error, setError] = React.useState('')
  const [blockingError, setBlockingError] = React.useState('')
  const storedMessage = validationActive && !inputProps.disabled ? storedInputError?.message : ''
  const storedDraft = storedInputError?.draft !== undefined ? String(storedInputError.draft) : null
  const activeError = validationActive && !inputProps.disabled
    ? (blockingError || storedMessage || '')
    : ''
  const displayedDraft = !blockingError && storedMessage && storedDraft !== null
    ? storedDraft
    : draft
  const isInvalid = Boolean(activeError)
  const displayedError = error || activeError

  React.useEffect(() => {
    if (editing || error || storedMessage) return
    setDraft(formatNumberValue(value))
  }, [value, editing, error, storedMessage])

  React.useLayoutEffect(() => {
    const hadStoredMessage = previousStoredMessageRef.current
    previousStoredMessageRef.current = storedMessage

    if (hadStoredMessage && !storedMessage && blockingError) {
      skipNextErrorPublishRef.current = true
      setDraft(formatNumberValue(value))
      setEditing(false)
      setError('')
      setBlockingError('')
      return
    }

    if (storedMessage && storedDraft !== null && !blockingError && draft !== storedDraft) {
      setDraft(storedDraft)
    }
  }, [blockingError, draft, storedDraft, storedMessage, value])

  React.useEffect(() => {
    if (skipNextErrorPublishRef.current) {
      skipNextErrorPublishRef.current = false
      return undefined
    }
    if (lastValidationResetKey.current !== inputValidationResetKey) return
    if (!validationActive) return undefined
    const message = validationActive && !inputProps.disabled ? blockingError : ''
    if (!message && storedMessage) return undefined
    setInputError(inputErrorId, message
      ? { message, label: inputErrorLabel, target: validationTarget || { validationId: inputErrorId }, draft }
      : '')
    return undefined
  }, [blockingError, draft, inputErrorId, inputErrorLabel, inputProps.disabled, inputValidationResetKey, setInputError, storedMessage, validationActive, validationTarget])

  React.useEffect(() => () => {
    if (validationTarget?.kind !== 'employee') setInputError(inputErrorId, '', { track: false })
  }, [inputErrorId, setInputError, validationTarget?.kind])

  React.useEffect(() => {
    if (lastValidationResetKey.current === inputValidationResetKey) return
    lastValidationResetKey.current = inputValidationResetKey
    setDraft(formatNumberValue(value))
    setEditing(false)
    setError('')
    setBlockingError('')
  }, [inputValidationResetKey, value])

  const validate = React.useCallback((raw) => {
    const trimmed = String(raw ?? '').trim()
    if (trimmed === '') {
      return emptyIsValid
        ? { valid: true, value: emptyValue, display: '' }
        : { valid: false, error: emptyMessage }
    }

    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed)) {
      return { valid: false, error: invalidMessage }
    }
    if (integer && !Number.isInteger(parsed)) {
      return { valid: false, error: 'Enter a whole number.' }
    }
    if (min !== undefined && parsed < min) {
      return { valid: false, error: minMessage || `Enter ${min} or more.` }
    }
    if (max !== undefined && parsed > max) {
      return { valid: false, error: maxMessage || `Enter ${max} or less.` }
    }

    return { valid: true, value: parsed, display: String(parsed) }
  }, [emptyIsValid, emptyValue, emptyMessage, invalidMessage, integer, max, maxMessage, min, minMessage])

  const handleChange = (event) => {
    const nextDraft = event.target.value
    setEditing(true)
    setDraft(nextDraft)

    const result = validate(nextDraft)
    if (result.valid) {
      setError('')
      setBlockingError('')
      onValueChange?.(result.value)
      setInputError(inputErrorId, '', { track: false })
    } else {
      setError('')
      setBlockingError(result.error)
      if (validationActive && !inputProps.disabled) {
        setInputError(inputErrorId, {
          message: result.error,
          label: inputErrorLabel,
          target: validationTarget || { validationId: inputErrorId },
          draft: nextDraft,
        })
      }
    }
  }

  const handleBlur = (event) => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false
      onBlur?.(event, { valid: true, value })
      return
    }

    setEditing(false)
    const result = validate(draft)
    if (result.valid) {
      setError('')
      setBlockingError('')
      setDraft(result.display)
      if (!Object.is(result.value, value)) onValueChange?.(result.value)
      setInputError(inputErrorId, '', { track: false })
    } else {
      setError(result.error)
      setBlockingError(result.error)
      if (validationActive && !inputProps.disabled) {
        setInputError(inputErrorId, {
          message: result.error,
          label: inputErrorLabel,
          target: validationTarget || { validationId: inputErrorId },
          draft,
        })
      }
    }
    onBlur?.(event, result)
  }

  const handleFocus = () => {
    setEditing(true)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      skipNextBlurCommitRef.current = true
      setDraft(formatNumberValue(value))
      setError('')
      setBlockingError('')
      setInputError(inputErrorId, '', { track: false })
      setEditing(false)
      event.currentTarget.blur()
    }
    inputProps.onKeyDown?.(event)
  }

  return (
    <div className={cn('space-y-1', containerClassName)}>
      <Input
        {...inputProps}
        type="text"
        inputMode={inputMode || (integer ? 'numeric' : 'decimal')}
        value={displayedDraft}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        aria-invalid={isInvalid ? true : undefined}
        data-validation-id={inputErrorId}
        title={(isInvalid && displayedError) || inputProps.title}
        className={className}
      />
      {showError && isInvalid && displayedError && (
        <p className={cn('text-xs text-destructive', errorClassName)}>
          {displayedError}
        </p>
      )}
    </div>
  )
}

export { NumberInput }
